import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { RégionStore } from '../../../administration/gestion-sites/store/région.store';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { Étape } from '../../../référentiel-métier-parcours/models/étape.model';
import { ButtonAddNewComponent } from '../../../shared/components/button-add-new/button-add-new.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../shared/components/expansion-panel/expansion-panel.component';
import { ÉTAT_GROUPE } from '../../enum/état-groupe.enum';
import { Groupe } from '../../models/groupe.model';
import { ConvertEtatGroupeToStringPipe } from '../../pipes/convert-etat-groupe-to-string.pipe';
import { GetEtatGroupeVariantPipe } from '../../pipes/get-etat-groupe-variant.pipe';
import { GroupeCreateRequest } from '../../requests/groupe-create-request';
import { GroupeStore } from '../../store/groupe.store';
import { ResumeEtapeComponent } from '../resume-etape/resume-etape.component';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-form-groupes',
  imports: [
    ReactiveFormsModule,
    ExpansionPanel,
    ButtonAddNewComponent,
    HlmInputImports,
    LabelRequiredDirective,
    HlmSelectImports,
    BrnSelectImports,
    HlmFieldImports,
    HlmLabelImports,
    TrierParPipe,
    HlmRadioGroupImports,
    HlmSwitchImports,
    HlmButtonImports,
    ConvertEtatGroupeToStringPipe,
    ChipComponent,
    GetEtatGroupeVariantPipe,
    ResumeEtapeComponent,
  ],
  templateUrl: './form-groupes.component.html',
})
export class FormGroupesComponent implements OnInit {
  readonly #régionStore = inject(RégionStore);
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #référentielStore = inject(ReferentielStore);
  readonly #groupeStore = inject(GroupeStore);

  readonly idSite = input.required<string>();
  readonly cartographie = input.required<Cartographie>();
  readonly etape = input.required<Étape>();
  readonly readonly = input<boolean>();

  readonly referentiels = computed(() => this.#référentielStore.entities());
  readonly groupes = linkedSignal(() => this.etape().groupes ?? []);
  readonly publicsCibles = computed(() => this.etape().publicsCibles);

  readonly form = this.#fb.group({
    groupes: this.#fb.array([]),
  });
  readonly etatGroupe = ÉTAT_GROUPE;

  readonly groupeActions: Signal<ExpansionPanelMenuAction<number>[]> = computed(() =>
    !this.readonly()
      ? [
          {
            icon: 'lucideTrash',
            label: 'Supprimer le groupe',
            class: 'invisible group-hover/item:visible',
            action: (idx) => this.#supprimerGroupe(idx!),
          },
        ]
      : [],
  );

  readonly groupesASupprimer: string[] = [];

  constructor() {
    effect(() => {
      const groupes = this.groupes();
      const readonly = this.readonly();

      if (groupes.length > 0) {
        this.groupesForm.clear();

        groupes.forEach((groupe) => this.ajouterGroupe(groupe));
      } else if (!readonly) {
        this.ajouterGroupe();
      }
      if (readonly) this.form.disable();
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterGroupe(groupe?: Groupe) {
    const groupeForm: FormGroup<GroupeForm> = this.#fb.group({
      id: this.#fb.control(groupe?.id),
      sectionNationale: this.#fb.nonNullable.control({ value: groupe?.sectionNationale ?? false, disabled: true }),
      metiers: this.#fb.nonNullable.control(groupe?.idsReferentiels ?? ([] as string[]), [Validators.required]),
      recrutesMaximum: this.#fb.control(groupe?.recrutesMaximum ?? null, [Validators.required]),
      placesMinimum: this.#fb.control(groupe?.placesMinimum ?? null, [Validators.required]),
      placesMaximum: this.#fb.control(groupe?.placesMaximum ?? null, [Validators.required]),
      annee: this.#fb.nonNullable.control(groupe?.annee ?? (1 as 1 | 2 | 3), [Validators.required]),
      capp: this.#fb.nonNullable.control({
        value: groupe?.capp ?? (this.etape().typesContrat ?? []).includes('apprentissage'),
        disabled: true,
      }),
      ouvert: this.#fb.nonNullable.control(groupe?.ouvert ?? false),
      etat: this.#fb.nonNullable.control(groupe?.etat ?? ÉTAT_GROUPE.A_FAIRE),
      publicsCibles: this.#fb.nonNullable.control(groupe?.publicsCibles ?? []),
    });
    if (groupe?.etat === ÉTAT_GROUPE.VALIDE) groupeForm.disable();
    this.groupesForm.push(groupeForm);
  }

  envoyerGroupeAValidation(idxForm: number) {
    const groupeForm = this.groupesForm.at(idxForm);
    const groupe = groupeForm.value as Groupe;
    const toastrId = uuidv7();

    this.#groupeStore.modifier({
      id: groupe.id,
      data: { ...groupe, etat: ÉTAT_GROUPE.A_VALIDER },
      next: () => {
        this.#régionStore.charger(this.cartographie().id);
        this.groupes.update((groupes) =>
          groupes.map((x) => {
            if (groupe.id !== x.id) return x;

            x.etat = ÉTAT_GROUPE.A_VALIDER;
            return x;
          }),
        );
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: `Groupe correctement envoyé à validation`,
            id: toastrId,
          },
          id: toastrId,
        });
      },
    });
  }

  #supprimerGroupe(index: number) {
    const groupe = this.groupesForm.at(index).value;

    this.groupesForm.removeAt(index);

    if (groupe.id) {
      this.groupesASupprimer.push(groupe.id);
    }
  }

  onSubmit() {
    const toastrId = uuidv7();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: 'Merci de renseigner tous les champs marqués comme requis.',
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }

    const data: GroupeCreateRequest = {
      siteEtape: { idSite: this.idSite(), idEtape: this.etape().id },
      groupes: this.groupesForm.getRawValue().map((values) => ({
        sectionNationale: values.sectionNationale,
        annee: values.annee,
        capp: values.capp,
        idsReferentiels: values.metiers,
        ouvert: values.ouvert,
        placesMaximum: values.placesMaximum,
        placesMinimum: values.placesMinimum,
        recrutesMaximum: values.recrutesMaximum,
        etat: values.etat,
        publicsCibles: values.publicsCibles,
      })),
    };

    this.#groupeStore.créer({
      data,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des groupes réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#régionStore.charger(this.cartographie().id);
        this.#sidepanelService.close();
      },
    });

    if (this.groupesASupprimer.length) {
      this.groupesASupprimer.forEach((idGroupe) => this.#groupeStore.supprimer(idGroupe));
    }
  }

  get groupesForm() {
    return this.form.get('groupes') as FormArray<FormGroup<GroupeForm>>;
  }
}

interface GroupeForm {
  id: FormControl<string | null | undefined>;
  sectionNationale: FormControl<boolean>;
  metiers: FormControl<string[]>;
  recrutesMaximum: FormControl<number | null>;
  placesMinimum: FormControl<number | null>;
  placesMaximum: FormControl<number | null>;
  annee: FormControl<1 | 2 | 3>;
  capp: FormControl<boolean>;
  ouvert: FormControl<boolean>;
  etat: FormControl<ÉTAT_GROUPE>;
  publicsCibles: FormControl<string[]>;
}
